import { create } from "zustand";

import { onSessionEnd } from "@utils/sessionTeardown";

export interface ToastState {
  isVisible: boolean;
  title?: string;
  description?: string;
  content: React.ReactNode | null;
  isExpanded: boolean;
  id: string; // unique id for the toast
  // While true the header's close button minimises instead, so content with
  // work in flight can't be unmounted by it (MDRS-550).
  minimiseOnDismiss?: boolean;
  onClose?: () => void; // Runs when the toast is closed (not when replaced)
}

interface ToastStore {
  // State
  isVisible: boolean;
  title?: string;
  description?: string;
  content: React.ReactNode | null;
  isExpanded: boolean;
  id: string;
  minimiseOnDismiss: boolean;
  onClose?: () => void;

  // Actions
  showToast: (toast: Omit<ToastState, "isVisible" | "isExpanded">) => void;
  closeToast: () => void; // Completely removes the toast (resets state)
  toggleExpand: () => void;
  minimise: () => void;
  setMinimiseOnDismiss: (minimiseOnDismiss: boolean) => void;
  setTitle: (title: string) => void;
}

export const useToastStore = create<ToastStore>((set, get) => ({
  // Initial state
  isVisible: false,
  title: "",
  description: "",
  content: null,
  isExpanded: true,
  id: "",
  minimiseOnDismiss: false,
  onClose: undefined,

  // Actions
  showToast: (toast) =>
    set({
      isVisible: true,
      title: toast.title,
      description: toast.description,
      content: toast.content,
      isExpanded: true, // Start expanded by default
      id: toast.id || Date.now().toString(),
      minimiseOnDismiss: toast.minimiseOnDismiss ?? false,
      onClose: toast.onClose,
    }),
  closeToast: () => {
    // Reset first, so an onClose that shows a new toast isn't wiped by it.
    const { onClose } = get();
    set({
      isVisible: false,
      title: "",
      description: "",
      content: null,
      isExpanded: true,
      id: "",
      minimiseOnDismiss: false,
      onClose: undefined,
    }); // Completely reset the toast state
    onClose?.();
  },
  toggleExpand: () => set((state) => ({ isExpanded: !state.isExpanded })),
  minimise: () => set({ isExpanded: false }),
  setMinimiseOnDismiss: (minimiseOnDismiss) => set({ minimiseOnDismiss }),
  setTitle: (title) => set({ title }),
}));

// The toast is mounted outside the router, so nothing else tears it down on
// logout. Every logout path runs this before it navigates, so a bundle
// upload's unload warning never fires for a session that has already ended
// (MDRS-550) — and no session's content outlives it.
onSessionEnd(() => useToastStore.getState().closeToast());
